import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import Select, { SingleValue } from "react-select";
import { days, meals } from "../data";
import { PermissionCheck } from "src/helpers/PermissionCheck";
import API_PATHS from "src/api/apiPaths";
import API_HEADERS from "src/api/apiConfig";
import ListResponse from "src/helpers/ListResponse";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import AddNewAssortment from "./AddNewAssortment";
import EditAssortment from "./EditAssortment";
import ViewAssortment from "./ViewAssortment";
import SearchFilter from "src/helpers/SearchFilter";
import CustomPagination from "src/helpers/CustomPaginate";
import Loading from "src/helpers/Loading";
import useSearchSubmit from "src/hooks/useSearchSubmit";
import * as ConfirmPopup from "../../../../helpers/ConfirmPopup";

const Assortments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Assortment list
  const [assortments, setAssortments] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Search
  const {
    searchInputs,
    searchBtnLoading,
    searched,
    page,
    setSearchBtnLoading,
    handleSubmit,
    setPage,
  } = useSearchSubmit();
  const [ageGroupsList, setFoodAgeGroupsList] = useState<any>([]);
  const [ageGroupSearch, setAgeGroupSearch] =
    useState<SingleValue<{ value: string; label: string }>>(null);
  const [daySearch, setDaySearch] =
    useState<SingleValue<{ value: string; label: string }>>(null);
  const [mealSearch, setMealSearch] =
    useState<SingleValue<{ value: string; label: string }>>(null);

  // Modal
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [modalId, setModalId] = useState<number | null>(null);

  const getAssortments = async (): Promise<void> => {
    let searchParams: any = searchInputs;
    let request: any = API_PATHS.assortmentList;

    request = SearchFilter(searchParams, request);

    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "GET",
        API_HEADERS.authenticated
      );

      setAssortments(response.assortments);
      setLoading(false);
      !searched && page === 0 && getFoodAgeGroupData();
      setSearchBtnLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      navigate(-1);
    }
  };

  const getFoodAgeGroupData = async (): Promise<void> => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.foodAgeGroupsNames,
        "GET",
        API_HEADERS.authenticated
      );

      const ageGroupsArr = [];
      for (const [key, value] of Object.entries(response.foodAgeGroup)) {
        ageGroupsArr.push({
          label: value,
          value: key,
        });
      }

      setFoodAgeGroupsList(ageGroupsArr);
    } catch (error: any) {}
  };

  useEffect(() => {
    getAssortments();
  }, [searchInputs]);

  const deleteAssortment = (assortmentId: number, assortmentTitle: string) => {
    ConfirmPopup.deletePopup(
      t("meal_delete_assortment_title"),
      t("meal_delete_assortment_type"),
      assortmentTitle
    ).then(async (response) => {
      if (response.isConfirmed) {
        try {
          const response: any = await makeApiCall<ResponseType>(
            API_PATHS.assortmentDelete(assortmentId),
            "DELETE",
            API_HEADERS.authenticated
          );
          if (assortments.data.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            getAssortments();
          }
          FLASH_MESSAGES.successMsg(response.success);
        } catch (error: any) {
          if (error.response.data.error) {
            FLASH_MESSAGES.errorMsg(error.response.data.error);
          }
        }
      }
    });
  };

  return (
    <>
      <form
        onSubmit={(e) =>
          handleSubmit(e, {
            ...(ageGroupSearch && { food_age_group_id: ageGroupSearch.value }),
            ...(daySearch && { day: daySearch.value }),
            ...(mealSearch && { meal: mealSearch.value }),
          })
        }
        className="row"
      >
        {PermissionCheck("food-age-group-names") ? (
          <div className="form-block col-12 col-sm-6 col-md-3 col-xxl-2">
            <label>{t("meal_assortments_list_age_group_label")}</label>
            <Select
              placeholder={t("meal_assortments_list_age_group_placeholder")}
              className="form-react-select"
              classNamePrefix="select_input"
              name="food_age_group_id"
              onChange={(e) => setAgeGroupSearch(e)}
              isClearable={true}
              isSearchable={false}
              options={ageGroupsList}
              value={ageGroupSearch}
            />
          </div>
        ) : (
          ""
        )}
        <div className="form-block col-12 col-sm-6 col-md-3 col-xxl-2">
          <label>{t("meal_assortments_list_day_label")}</label>
          <Select
            placeholder={t("meal_assortments_list_day_placeholder")}
            className="form-react-select"
            classNamePrefix="select_input"
            name="day"
            onChange={(e) => setDaySearch(e)}
            isClearable={true}
            isSearchable={false}
            options={days}
            value={daySearch}
          />
        </div>
        <div className="form-block col-12 col-sm-6 col-md-3 col-xxl-2">
          <label>{t("meal_assortments_list_meal_label")}</label>
          <Select
            placeholder={t("meal_assortments_list_meal_placeholder")}
            className="form-react-select"
            classNamePrefix="select_input"
            name="meal"
            onChange={(e) => setMealSearch(e)}
            isClearable={true}
            isSearchable={false}
            options={meals}
            value={mealSearch}
          />
        </div>
        <div className="col-12 col-sm-6 col-md-3 col-xxl-2 mb-1">
          {searchBtnLoading ? (
            <button type="button" className="mt-4 button-orange has-icon">
              <i className="icon spinner-icon" />
              {t("meal_assortments_list_searching_button")}
            </button>
          ) : (
            <button type="submit" className="mt-4 button-orange has-icon">
              <i className="icon search-icon" />
              {t("meal_assortments_list_search_button")}
            </button>
          )}
        </div>
      </form>
      {PermissionCheck("assortment-store") && (
        <div className="w-100 d-flex justify-content-start justify-content-md-end my-5 mt-md-0">
          <button
            className="button-blue has-icon"
            onClick={() => setShowAddModal(true)}
          >
            <i className="icon add-icon" />
            {t("meal_add_new_assortment")}
          </button>
          {showAddModal && (
            <AddNewAssortment
              onShowModal={showAddModal}
              setShowModal={(e) => setShowAddModal(e)}
              setModified={() => getAssortments()}
            />
          )}
        </div>
      )}
      <div className="table-list mb-5">
        <table role="table" className="w-100">
          <thead>
            <tr role="row">
              <th
                style={{ width: "40%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon assortment-icon" />
                {t("meal_assortments_table_name")}
              </th>
              <th
                style={{ width: "25%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon day-icon" />
                {t("meal_assortments_table_day")}
              </th>
              <th
                style={{ width: "20%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon meal-icon" />
                {t("meal_assortments_table_meal")}
              </th>
              <th
                style={{ width: "15%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon actions-icon" />
                {t("meal_assortments_table_action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="text-center" colSpan={4}>
                  <Loading />
                </td>
              </tr>
            ) : (
              assortments?.data?.map((assortment: any) => (
                <tr key={assortment.id}>
                  <td
                    role="cell"
                    className="text-left bold-text"
                    data-label={t("meal_assortments_table_name")}
                  >
                    {assortment.name}
                  </td>
                  <td
                    role="cell"
                    className="text-left bold-text"
                    data-label={t("meal_assortments_table_day")}
                  >
                    {assortment.day}
                  </td>
                  <td
                    role="cell"
                    className="text-left bold-text"
                    data-label={t("meal_assortments_table_meal")}
                  >
                    {assortment.meal}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("meal_assortments_table_action")}
                  >
                    <div className="actions">
                      {PermissionCheck("assortment-update") && (
                        <>
                          <button
                            className="button-unstyled-icon"
                            onClick={() => {
                              setShowEditModal(true);
                              setModalId(assortment.id);
                            }}
                          >
                            <i className="icon edit-icon" />
                          </button>
                          {showEditModal && modalId === assortment.id && (
                            <EditAssortment
                              onShowModal={showEditModal}
                              setShowModal={(e) => {
                                setShowEditModal(e);
                                setModalId(null);
                              }}
                              assortmentId={assortment.id}
                              setModified={() => getAssortments()}
                            />
                          )}
                        </>
                      )}
                      {PermissionCheck("assortment-edit") && (
                        <button
                          className="button-unstyled-icon"
                          onClick={() => {
                            setShowViewModal(true);
                            setModalId(assortment.id);
                          }}
                        >
                          <i className="icon show-icon" />
                        </button>
                      )}
                      {showViewModal && modalId === assortment.id && (
                        <ViewAssortment
                          onShowModal={showViewModal}
                          setShowModal={(e) => {
                            setShowViewModal(e);
                            setModalId(null);
                          }}
                          assortmentId={assortment.id}
                        />
                      )}
                      {PermissionCheck("assortment-delete") && (
                        <button
                          className="button-unstyled-icon"
                          onClick={() =>
                            deleteAssortment(assortment.id, assortment.name)
                          }
                        >
                          <i className="icon delete-icon" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <ListResponse
          totalItems={assortments?.data?.length}
          loading={loading}
        />
      </div>
      <CustomPagination data={assortments} setActivePage={(e) => setPage(e)} />
    </>
  );
};

export default Assortments;
